<template>
    <b-navbar toggleable="lg" type="dark" variant="primary">
        <div class="lx-header-brand">
            <b-navbar-brand href="/app/dashboard/" title="Dashboard"></b-navbar-brand>
            <b-navbar-toggle target="lx-header-collapse">
                <font-awesome-icon :icon="['fas', 'bars']"></font-awesome-icon>
            </b-navbar-toggle>
        </div>
        <div class="lx-header-right lx-header-right-mobile">
            <b-navbar-nav>
                <b-nav-item
                    v-for="(item, index) in rightMenuItems"
                    :key="'rightMenuItemsMobile' + index"
                    :href="item.href"
                    :target="item.target"
                >
                    <font-awesome-icon
                        v-if="item.icon"
                        :icon="['fas', item.icon]"
                        size="lg"
                        class="mr-1"
                        :title="item.text"
                    ></font-awesome-icon>
                </b-nav-item>
                <header-dropdown
                    v-for="(menu, index) in rightMenuDropdowns"
                    :key="'rightMenuDropdownsMobile' + index"
                    variant="link"
                    toggle-class="text-decoration-none"
                    no-caret
                    class="dropdown-menu-header"
                    right
                    :menu="menu"
                ></header-dropdown>
                <header-notification class="dropdown-menu-header"></header-notification>
                <header-account></header-account>
            </b-navbar-nav>
        </div>
        <b-collapse id="lx-header-collapse" is-nav>
            <b-navbar-nav>
                <b-nav-item
                    v-for="(item, index) in mainMenuItems"
                    :key="'mainMenuItems' + index"
                    :href="item.href"
                    :target="item.target"
                >
                    <font-awesome-icon
                        v-if="item.icon"
                        :icon="['fas', item.icon]"
                        size="lg"
                        class="mr-1"
                    ></font-awesome-icon>
                    {{ item.text }}
                </b-nav-item>
                <header-dropdown
                    v-for="(menu, index) in mainMenuDropdowns"
                    :key="'mainMenuDropdowns' + index"
                    :menu="menu"
                    :data-intercom-target="menu.text"
                ></header-dropdown>
            </b-navbar-nav>
            <header-search class="lx-header-search-mobile"></header-search>
        </b-collapse>
        <div class="lx-header-right lx-header-right-desktop ml-auto">
            <header-search v-if="userID"></header-search>
            <b-navbar-nav>
                <b-nav-item
                    v-for="(item, index) in rightMenuItems"
                    :key="'rightMenuItems' + index"
                    :href="item.href"
                    :target="item.target"
                    :data-intercom-target="item.text"
                >
                    <font-awesome-icon
                        v-if="item.icon"
                        :icon="['fas', item.icon]"
                        size="lg"
                        class="mr-1"
                        :title="item.text"
                    ></font-awesome-icon>
                </b-nav-item>
                <header-dropdown
                    v-for="(menu, index) in rightMenuDropdowns"
                    :key="'rightMenuDropdowns' + index"
                    variant="link"
                    toggle-class="text-decoration-none"
                    class="dropdown-menu-header"
                    no-caret
                    right
                    :menu="menu"
                    :data-intercom-target="menu.text"
                ></header-dropdown>
                <header-notification
                    data-intercom-target="Notification"
                    class="dropdown-menu-header"
                ></header-notification>
                <header-account data-intercom-target="Account"></header-account>
            </b-navbar-nav>
            <print-calendar-modal></print-calendar-modal>
            <create-property-modal></create-property-modal>
            <dashboard-details-modal-form
                modal-id="create-dashboard-modal"
                :create="createDashboard"
                @success-create="handleSuccessDashboardCreate"
            />
            <modal ok-only title="Error" id="team-member-module-error-modal">
                <p>The property owner or manager has not subscribed to this feature.</p>
                <p>
                    To enable this functionality please contact the property owner or manager and request that they
                    enable this functionality by logging in to their Lodgix.com account and clicking on the "Manage
                    Billing" menu item.
                </p>
            </modal>
            <modal
                id="failed-transactions-modal"
                title="Failed Transactions"
                no-close-on-backdrop
                hide-header-close
                no-close-on-esc
            >
                <span class="text-danger font-weight-bold">
                    You have
                    <span v-if="failedTransactionsCount">
                        <strong>{{ failedTransactionsCount }}</strong> unresolved
                    </span>
                    <span v-if="undergoingResolutionTransactionsCount">
                        <span v-if="failedTransactionsCount"> and</span>
                        <strong>{{ undergoingResolutionTransactionsCount }}</strong> undergoing resolution
                    </span>
                    transaction{{ failedTransactionsPlural }}.
                </span>
                <template #modal-footer="{ ok }">
                    <div class="w-100">
                        <b-button
                            variant="outline-primary"
                            class="float-left"
                            target="_blank"
                            :href="failedTransactionsHref"
                        >
                            View Failed Transactions
                        </b-button>
                        <b-button variant="outline-primary" class="float-right ml-2" @click="ok()"> OK </b-button>
                    </div>
                </template>
            </modal>
            <notification-modal
                showMarkAsRead
                :subject="notificationSubject"
                :message="notificationMessage"
                :modalId="notificationModalId"
                :notificationId="notificationId"
                :date="notificationDate"
                :href="notificationHref"
            ></notification-modal>
        </div>
    </b-navbar>
</template>

<script>
    import API from '../../../api';
    import { DASHBOARD_DASHBOARD_LIST_ENDPOINT, FCM_DEVICE_ENDPOINT, NOTIFICATION_ENDPOINT } from '../../../endpoints';
    import { NOTIFICATION_DROPDOWN_MAX } from '../../../constants';
    import DashboardDetailsModalForm from '../../dashboard/list/dashboard-details-modal-form.vue';
    import HeaderDropdown from '../dropdown/header-dropdown.vue';
    import HeaderSearch from '../search/header-search.vue';
    import HeaderAccount from '../account/header-account.vue';
    import HeaderNotification from '../notification/header-notification.vue';
    import PrintCalendarModal from '../print-calendar-modal/print-calendar-modal.vue';
    import CreatePropertyModal from '../create-property-modal/create-property-modal.vue';
    import Modal from '../../generic/modal/modal.vue';
    import { mapGetters, mapMutations } from 'vuex';
    import { setupFCM, isSupportedBrowser } from '../../../utils/firebase';
    import NotificationModal from '../../notification/notification-modal.vue';

    export default {
        name: 'HeaderNavBar',
        components: {
            DashboardDetailsModalForm,
            HeaderDropdown,
            HeaderSearch,
            HeaderAccount,
            HeaderNotification,
            PrintCalendarModal,
            CreatePropertyModal,
            Modal,
            NotificationModal,
        },
        computed: {
            ...mapGetters('header', [
                'userID',
                'mainMenuItems',
                'mainMenuDropdowns',
                'rightMenuItems',
                'rightMenuDropdowns',
                'failedTransactionsHref',
                'failedTransactionsCount',
                'undergoingResolutionTransactionsCount',
                'failedTransactionsShowModalNotification',
            ]),
            failedTransactionsPlural() {
                return this.failedTransactionsCount > 1 || this.undergoingResolutionTransactionsCount > 1 ? 's' : '';
            },
        },
        mounted() {
            (async () => {
                try {
                    await this.$store.dispatch('header/fetchAccount');
                    await this.FCM();
                    await this.$store.dispatch('header/fetchNotifications');
                } catch (err) {
                    console.log(`Could not fetch account data in header nav bar: ${err}`);
                }
            })();
            this.$store.dispatch('header/fetchFailedTransactions');
        },
        methods: {
            ...mapGetters('header', ['deviceList', 'fcmClientConfig', 'fcmVapidKey', 'isImpersonated']),
            ...mapMutations('header', ['setDevices', 'setToken', 'setNotificationList', 'setIsNotificationSupported']),
            async FCM() {
                let isSupported = await isSupportedBrowser();
                if (this.isImpersonated()) {
                    isSupported = false;
                }
                this.setIsNotificationSupported(isSupported);

                if (isSupported) {
                    await setupFCM(
                        this.fcmClientConfig(),
                        this.fcmVapidKey(),
                        (title, body, data) => {
                            const message = body.substring(0, 100) + (body.length >= 100 ? '...' : '');
                            this.infoActionToast({
                                text: message,
                                title: title,
                                callback: () => {
                                    this.notificationSubject = title;
                                    this.notificationMessage = body;
                                    this.notificationDate = new Date().toISOString();
                                    this.notificationId = data.message_id;
                                    if (data?.href) {
                                        this.notificationHref = data.href;
                                    } else {
                                        this.notificationHref = '';
                                    }
                                    this.$bvModal.show(this.notificationModalId);
                                },
                            });
                            setTimeout(async () => {
                                const notificationList = await API.get(`${NOTIFICATION_ENDPOINT()}notifications/`, {
                                    params: {
                                        offset: 0,
                                        limit: NOTIFICATION_DROPDOWN_MAX,
                                        sort: 'date',
                                        desc: true,
                                        is_read: false,
                                    },
                                });
                                this.setNotificationList(notificationList.data);
                            }, 1000);
                        },
                        async (token) => {
                            this.setToken(token);
                            if (this.deviceList().length <= 0) {
                                const list = await API.get(FCM_DEVICE_ENDPOINT());
                                this.setDevices(list.data);
                            }
                            const devicesList = await this.deviceList();
                            const device = devicesList.find((device) => device.registration_id === token);
                            if (!device) {
                                let deviceName = navigator.userAgent;
                                await API.post(FCM_DEVICE_ENDPOINT(), {
                                    registration_id: token,
                                    type: 'web',
                                    name: deviceName,
                                });
                            }
                        }
                    );
                }

                const notificationList = await API.get(`${NOTIFICATION_ENDPOINT()}notifications/`, {
                    params: {
                        offset: 0,
                        limit: NOTIFICATION_DROPDOWN_MAX,
                        sort: 'date',
                        desc: true,
                        is_read: false,
                    },
                });
                this.setNotificationList(notificationList.data);
            },
            handleSuccessDashboardCreate(dashboard) {
                window.location = `/app/dashboard/?dashboard=${dashboard.id}`;
            },
            createDashboard(values) {
                return API.post(DASHBOARD_DASHBOARD_LIST_ENDPOINT(), values);
            },
        },
        watch: {
            failedTransactionsShowModalNotification(showModal) {
                if (showModal) {
                    this.$bvModal.show('failed-transactions-modal');
                }
            },
        },
        data() {
            return {
                notificationSubject: '',
                notificationMessage: '',
                notificationDate: '',
                notificationModalId: 'fcm-notification-modal',
                notificationId: '',
                notificationHref: '',
            };
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    :deep(.navbar-brand) {
        height: 31px;
        width: 100px;
        background-image: url(logo.svg);
        background-repeat: no-repeat;
    }

    .lx-header-right {
        display: flex;
    }

    .lx-header-right-mobile {
        // Avoid floating pont height which may trigger vertical scroll bar
        height: 38px;
    }

    @include media-breakpoint-down(md) {
        .lx-header-right-desktop {
            display: none;
        }

        .lx-header-brand {
            display: flex;
        }

        :deep(.navbar-brand) {
            width: 24px;
            vertical-align: middle;
        }

        :deep(.lx-header-search) {
            margin: 0.5rem 0;
        }

        .lx-header-right {
            flex-direction: column;
        }

        .lx-header-right :deep(.navbar-nav) {
            position: relative;
            flex-direction: row;
        }
    }

    @include media-breakpoint-up(lg) {
        .lx-header-right-mobile,
        .lx-header-search-mobile {
            display: none;
        }

        .lx-header-right {
            align-items: center;
        }

        :deep(.navbar-nav) {
            align-items: center;
        }

        .lx-header-right :deep(.navbar-nav) {
            margin-left: 1rem;
        }
    }

    @include media-breakpoint-only(lg) {
        :deep(.navbar-brand) {
            margin-right: 0;
            width: 24px;
        }
    }

    // Overwriting bootstrap style
    :deep(.navbar-toggler) {
        border: none;
        padding: 0;
        color: #ffffff !important;
        outline: none !important;
    }

    .lx-header-right-mobile :deep(.nav-link) {
        padding: 0.5rem !important;
    }

    :deep(.nav-link) {
        color: #ffffff !important;
    }

    :deep(.nav-link:hover, .nav-link:active) {
        color: rgba(255, 255, 255, 0.75) !important;
    }
</style>
